// catalog domain types. Split out of types/index.ts (2,600 lines) on 2026-07-03.
// Import via '@/types' as before — index.ts re-exports everything.


import type { PricingScheme } from './access.types';

export interface AuditEntry {
  id: string
  seq: string
  actorId: string | null
  actorType: 'user' | 'admin' | 'system'
  actorEmail: string | null
  actorName: string | null
  actorRole: string | null
  action: string
  entityType: string
  entityId: string | null
  organizationId: string | null
  shopId: string | null
  method: string | null
  route: string | null
  correlationId: string | null
  latencyMs: number | null
  metadata: Record<string, unknown> | null
  ipAddress: string | null
  userAgent: string | null
  status: 'success' | 'failure' | 'error'
  statusCode: number | null
  source: 'auto' | 'manual' | 'pos-event' | 'system'
  prevHash: string | null
  contentHash: string | null
  createdAt: string
}

export interface AuditListMeta {
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface AuditActionCount {
  action: string
  count: number
}

export interface AuditChainResult {
  ok: boolean
  checked: number
  brokenAtSeq: string | null
  reason: string | null
}

export interface AuditListResponse {
  data: AuditEntry[]
  meta: AuditListMeta
}

export interface AuditQueryParams {
  page?: number
  limit?: number
  actorId?: string
  action?: string
  actionPrefix?: string
  entityType?: string
  entityId?: string
  shopId?: string
  source?: AuditEntry['source']
  status?: AuditEntry['status']
  correlationId?: string
  search?: string
  startDate?: string
  endDate?: string
}

export interface LocationDetails {
  lat?: string
  long?: string
  city: string
  state: string
  address: string
  country?: string
  zipCode: string
}

export interface Classification {
  id: string
  name: string
  slug?: string
  isMj?: boolean
}

export interface Category {
  id: string
  name: string
  classificationId?: string
  classification?: { id: string; name: string }
  slug?: string
}

export interface Brand {
  id: string
  name: string
  slug?: string
  description?: string
  details?: string
  logo?: string
  image?: string
  images?: string[]
  website?: string
  wmSlug?: string
  /** Slug this brand carries in the external catalog, persisted on import.
   *  Differs from `slug`, which is generated from the display name and made
   *  unique per org — catalog queries must use this one. */
  catalogSlug?: string
  catalogBrandId?: number
  /** Relation counts from the API (`_count`), e.g. how many products use this brand. */
  _count?: { products?: number }
  platformBrandId?: string
  isPlatform?: boolean
  isActive?: boolean
  createdAt?: string
  updatedAt?: string
}

export enum StrainType {
  SATIVA = 'SATIVA',
  INDICA = 'INDICA',
  HYBRID = 'HYBRID',
}

export interface Strain {
  id: string
  name: string
  slug?: string
  description?: string
  image?: string
  images?: string[]
  imageUrl?: string
  strainType?: StrainType
  isPlatform?: boolean
  /** EffectTag enum members. */
  effects?: string[]
  /** Curated terpene names. */
  terpenes?: string[]
  /** Relation counts from the API (`_count`). Products link via the
   *  StrainProduct join, so the key is `strainProducts`, not `products`. */
  _count?: { strainProducts?: number }
}

export interface Tag {
  id: string
  name: string
  color?: string
  colorCode?: string
}

export interface QuickWeightPreset {
  label: string
  value: number
}

export interface QuickWeights {
  enabled: boolean
  presets: QuickWeightPreset[]
}

export interface UnitOfMeasurement {
  id: string
  name: string
  measurementType: 'WEIGHT' | 'COUNT' | 'VOLUME'
  shortForm: string
  quickWeights?: QuickWeights | null
}

export interface Product {
  id: string
  organizationId: string
  name: string
  compactName: string
  slug: string
  categoryId: string
  category?: Category
  brandId?: string
  brand?: Brand
  classificationId?: string
  classification?: { id: string; name: string }
  description?: string
  highlights?: string
  images: string[]
  isCannabis: boolean
  metrcCategoryStringId?: string
  isActive: boolean
  isSuspended: boolean
  suspendReason?: string
  suspendedAt?: string
  saleConfigs?: SaleConfig[]
  strains?: { strain: Strain }[]
  tags?: { tag: Tag }[]
  tagIds?: string[]
  createdAt: string
  updatedAt: string
  thcValue?: string | number | null
  thcUnit?: 'PERCENTAGE' | 'MG' | '%' | null
  cbdValue?: string | number | null
  cbdUnit?: 'PERCENTAGE' | 'MG' | '%' | null
  effects?: string[]
  terpenes?: string[]
  ecomBadge?: string | null
}

/** A sale config ↔ shop association row, as `/sale-configs/*` returns it. */
export interface SaleConfigShop {
  shopId: string
  shop?: { id: string; shopName?: string } | null
}

export interface SaleConfig {
  id: string
  organizationId: string
  /** Not returned by GET /sale-configs/{id} — read `shops` for the real links. */
  shopId?: string
  /** Every shop this config is available in. A config can serve several shops. */
  shops?: SaleConfigShop[]
  productId: string
  product?: Product
  sku: string
  name: string
  displayName: string
  sellType: 'WEIGHT' | 'COUNT' | 'VOLUME'
  sellUomId: string
  sellUom?: UnitOfMeasurement
  minQuantity: number
  maxQuantity?: number
  quantityStep: number
  defaultQuantity?: number
  unitWeight?: number
  unitWeightUomId?: string
  unitWeightUom?: UnitOfMeasurement
  pricingSchemeId?: string
  pricingScheme?: PricingScheme
  flatPriceCents?: number | null
  simplePriceCents?: number
  isActive: boolean
  isSuspended: boolean
  suspendReason?: string
  suspendedAt?: string
  hideFromMenu: boolean
  allowOnlineSale: boolean
  isDiscountable: boolean
  isDeliverable: boolean
  isSample: boolean
  isPromo: boolean
  createdAt: string
  updatedAt: string
}

export interface OperationHours {
  slots?: Record<string, { open: string; close: string }>
  open24X7?: boolean
}

export interface Shop {
  id: string
  organizationId?: string
  shopName: string
  shopCompactName?: string
  shopEmail?: string
  currency?: string
  timeZone?: string
  webSite?: string
  countryCode?: string
  phone?: string
  logo?: string
  lockedAt?: string | null
  jurisdictionId?: string | null
  createdAt?: string
  updatedAt?: string
  locationDetails?: LocationDetails
  operationHours?: OperationHours
  isMetrcEnabled?: boolean
  /// What the state license permits: medical-only, rec-only, or dual.
  dispensaryType?: 'MEDICAL' | 'RECREATIONAL' | 'BOTH'
}

// Catalog API types (external platform data)
export interface CatalogProduct {
  id?: string
  slug?: string
  name: string
  image?: string
  platform?: string
  description?: string
  classification?: string
  category?: string
  categoryId?: string
  categoryName?: string
  brandId?: string
  images?: string[]
  strains?: string[]
  strainReferences?: { slug: string; id: string }[]
  cannabinoids?: { thc?: string; cbd?: string }
  /** Where the potency came from: platform field, page scrape, or strain average. */
  cannabinoidsSource?: 'published' | 'extracted' | 'strain-typical'
  /** Present only when cannabinoidsSource is 'strain-typical'. */
  thcEstimate?: { median: number; min: number | null; max: number | null }
  isPlatform?: boolean
  isWeedmaps?: boolean
  avatar_image?: string
}

export interface CatalogBrand {
  id: string
  name: string
  logo?: string
  slug?: string
  wmSlug?: string
  wm_slug?: string
  description?: string
  isPlatform?: boolean
}

export interface CatalogStrain {
  id: string
  name: string
  slug?: string
  strainType?: string
  category?: string
  imageUrl?: string
  image?: string
  description?: string
  preview?: { description?: string }
  isPlatform?: boolean
  lf_id?: string
  /** Scored maps from the catalog API, e.g. { myrcene: { score: 0.6 } }. */
  effects?: Record<string, { strength?: number }>
  terps?: Record<string, { score?: number }>
}

export interface GeoapifyFeature {
  properties: {
    formatted?: string
    address_line1?: string
    address_line2?: string
    street?: string
    city?: string
    town?: string
    village?: string
    municipality?: string
    county?: string
    state?: string
    state_code?: string
    postcode?: string
    zip?: string
    country?: string
    lat?: number
    lon?: number
  }
  geometry?: {
    coordinates?: [number, number]
  }
}

// UOM wrapper from category UOM endpoint — can be { uom: UnitOfMeasurement } or plain UnitOfMeasurement
export interface UomWrapper {
  uom?: UnitOfMeasurement
}

// Sale config form payload
export interface SaleConfigPayload {
  productId: string
  sku?: string
  displayName?: string
  name?: string
  sellType: string
  sellUomId: string
  minQuantity: number
  maxQuantity: number | null
  quantityStep: number
  defaultQuantity: number | null
  unitWeight: number | null
  unitWeightUomId: string | null
  isActive: boolean
  allowOnlineSale: boolean
  isDiscountable: boolean
  isDeliverable: boolean
  isSample: boolean
  isPromo: boolean
  simplePriceCents?: number
  pricingSchemeId?: string
  shopIds?: string[]
}

/// Matches the backend's createPaginatedResponse envelope: counts live under
/// `meta`, not at the top level. (The old flat shape was a lie — every reader
/// of `.total` silently got undefined.)
export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/// Compliance-sync alert raised by the Metrc background workers
/// (GET /metrc/sync/alerts). Active = acknowledgedAt is null.
export interface MetrcAlert {
  id: string
  shopId: string
  organizationId: string
  type: string
  severity: 'INFO' | 'WARNING' | 'CRITICAL' | string
  title: string
  message: string
  metadata?: Record<string, unknown> | null
  acknowledgedAt: string | null
  createdAt: string
}
